import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    InputField,
    PrivacyDisclaimer,
    RoundedButton,
    RoundedToggleButton,
    PrivacyCheckbox,
    ToastSuccess,
    NotificationBanner,
    createClasses,
} from "@bloomberg/media-ui-core";
import { Lock } from "@bloomberg/media-ui-icons";
import { useAbba } from "@foundation/abba-client";
import { useRegUIClient } from "@foundation/reg-ui-client";

import { TranslatedMessage, useTranslateMessage } from "~i18n/translate";
import {
    selectUserEmail,
    selectUserSubscribedNewsletters,
} from "~redux/features/user-slice";
import useRedux from "~redux/hooks";
import { RequestStatus } from "~types/lineup";
import GoogleAnalyticsEventTracker from "~utils/GoogleAnalyticsEventTracker";

import { sendMessage, validEmail } from "../../utils";

import styles from "./NewsletterPhoenix.module.css";

const REGISTRATION_URL = process.env.NEXT_PUBLIC_REGISTRATION_URL;
const tracker = new GoogleAnalyticsEventTracker();

const COUNTRIES_REQUIRING_FULL_DISCLAIMER = ["GB", "FR", "BR", "CA"];
const COUNTRIES_TO_AUTOCHECK = ["US", ...COUNTRIES_REQUIRING_FULL_DISCLAIMER];

const INVALID_EMAIL_MESSAGE = "Invalid email address, please try again";
const SUSCRIPTIONS_PAGE = "https://www.bloomberg.com/subscriptions";

interface NewsletterPhoenixFormProps {
    id: string;
    isSubscriberOnly?: boolean;
    listId?: string;
}

type CtaButtonProps = {
    handleOnClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    handleOnSubscriberOnlyClick: (
        event: React.MouseEvent<HTMLButtonElement>
    ) => void;
    isSignedUp: boolean;
    isNewsletterSubscriberOnly: boolean;
    isUserSubscriber?: boolean;
    status: RequestStatus;
};

const CtaButton = (props: CtaButtonProps) => {
    const {
        handleOnClick,
        handleOnSubscriberOnlyClick,
        isUserSubscriber,
        isSignedUp,
        isNewsletterSubscriberOnly,
        status,
    } = props;
    const translateMessage = useTranslateMessage();

    const SignUpButton = useMemo(
        () => (
            <RoundedToggleButton
                className={createClasses(styles.signUp, {
                    [styles.signedUp]: isSignedUp,
                })}
                withIcon
                onToggle={
                    isUserSubscriber
                        ? handleOnSubscriberOnlyClick
                        : handleOnClick
                }
                inactiveText={translateMessage({
                    id: "signUp",
                    defaultMessage: "Sign Up",
                })}
                activeText={translateMessage({
                    id: "signedUp",
                    defaultMessage: "Signed Up",
                })}
                active={isSignedUp}
                disabled={status === RequestStatus.Pending}
            />
        ),
        [isUserSubscriber, isSignedUp, status, handleOnClick]
    );

    const SubscribeButton = useMemo(
        () => (
            <RoundedButton
                className={styles.subscribe}
                colorMode="dark"
                Icon={<Lock />}
                iconPosition="left"
                onClick={handleOnSubscriberOnlyClick}
                disabled={status === RequestStatus.Pending}
                version="phoenix"
            >
                <TranslatedMessage id="subscribe" defaultMessage="Subscribe" />
            </RoundedButton>
        ),
        [handleOnSubscriberOnlyClick, status]
    );

    if (!isNewsletterSubscriberOnly) {
        return SignUpButton;
    }

    return isUserSubscriber ? SignUpButton : SubscribeButton;
};

export const NewsletterPhoenixForm = ({
    id,
    isSubscriberOnly = false,
    listId,
}: NewsletterPhoenixFormProps) => {
    const { geoIpData } = useAbba();
    const { session } = useRegUIClient();
    const [anonEmail, setAnonEmail] = useState("");
    const [isChecked, setChecked] = useState(false);
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState(RequestStatus.NotStarted);
    const [isSignedUp, setIsSignedUp] = useState(false);

    const { useAppSelector } = useRedux();
    const userEmail = useAppSelector(selectUserEmail);
    const userSubscribedNewsletters = useAppSelector(
        selectUserSubscribedNewsletters
    );
    const translateMessage = useTranslateMessage();
    const translatedInvalidPleaseTryAgain = translateMessage({
        id: "invalidEmailPleaseTryAgain",
        defaultMessage: INVALID_EMAIL_MESSAGE,
    });

    useEffect(() => {
        if (
            listId &&
            userSubscribedNewsletters &&
            userSubscribedNewsletters.includes(listId)
        ) {
            setIsSignedUp(true);
        }
    }, [userSubscribedNewsletters]);

    useEffect(() => {
        if (COUNTRIES_TO_AUTOCHECK.includes(geoIpData?.countryCode || "")) {
            setChecked(true);
        }
    }, [geoIpData?.countryCode]);

    const subscribeToNewsletter = useCallback(
        (email: string) => {
            const params = {
                email,
                listId,
                optIn: isChecked ? "true" : "false",
            };

            sendMessage(
                `${REGISTRATION_URL}/api/newsletters/subscribe`,
                {
                    sourcePage: "HUB Page",
                },
                params
            )
                .then((response) => {
                    if (!response) {
                        setStatus(RequestStatus.Error);
                        return;
                    }

                    setStatus(RequestStatus.Done);
                    setIsSignedUp(true);

                    tracker.track({
                        event: "newsletter_signup",
                        newsletterSignupIds: listId,
                    });
                })
                .catch((error) => {
                    setStatus(RequestStatus.Error);
                    console.error(
                        "Error occurred while signing up for newsletter",
                        error
                    );
                });
        },
        [isChecked, listId]
    );

    const handleOnClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();

            setMessage("");

            if (!REGISTRATION_URL) {
                return;
            }

            const email = userEmail || anonEmail;
            setStatus(RequestStatus.Pending);

            if (!validEmail(email)[0]) {
                setMessage(translatedInvalidPleaseTryAgain);
                setStatus(RequestStatus.Error);

                return;
            }

            subscribeToNewsletter(email);
        },
        [anonEmail, userEmail, subscribeToNewsletter]
    );

    const isUserSubscriber = session?.isBBGSubscriber;
    const handleOnSubscriberOnlyClick = useCallback(() => {
        if (!userEmail || !isUserSubscriber) {
            window.open(SUSCRIPTIONS_PAGE, "_blank", "noopener,noreferrer");

            return;
        }

        subscribeToNewsletter(userEmail);
    }, [subscribeToNewsletter, isUserSubscriber, userEmail]);

    if (!listId) {
        console.error(
            `No listId provided for NewsletterPhoenixForm with id ${id}.`
        );
        return null;
    }

    const isError = status === RequestStatus.Error;
    const isInvalidEmail = message === translatedInvalidPleaseTryAgain;
    const shouldShowFormFields = !isSignedUp && !userEmail;

    return (
        <>
            {isError && !isInvalidEmail && (
                <NotificationBanner
                    title={translateMessage({
                        id: "sorrySomethingWentWrong",
                        defaultMessage: "Sorry, something went wrong.",
                    })}
                    text={translateMessage({
                        id: "pleaseTryAgain",
                        defaultMessage: "Please try again",
                    })}
                    variant="error"
                />
            )}
            {shouldShowFormFields && (
                <>
                    {!isSubscriberOnly && (
                        <InputField
                            id={`form-for-${id}`}
                            type="email"
                            placeholder={translateMessage({
                                id: "enterYourEmail",
                                defaultMessage: "Enter your email",
                            })}
                            className={createClasses(styles.email, {
                                [styles.error]: isInvalidEmail,
                            })}
                            onChange={(e) => setAnonEmail(e.target.value)}
                            helperText={message}
                            error={isInvalidEmail}
                            size="small"
                        />
                    )}
                    <PrivacyCheckbox
                        checkboxProps={{
                            id: `privacy-checkbox-${id}`,
                        }}
                        includeTextInHitArea
                        checked={isChecked}
                        onChange={() =>
                            setChecked((prevIsChecked) => !prevIsChecked)
                        }
                        className={styles.checkbox}
                        countryCode={geoIpData?.countryCode}
                    />
                </>
            )}
            <CtaButton
                handleOnClick={handleOnClick}
                handleOnSubscriberOnlyClick={handleOnSubscriberOnlyClick}
                isUserSubscriber={isUserSubscriber}
                isNewsletterSubscriberOnly={isSubscriberOnly}
                isSignedUp={isSignedUp}
                status={status}
            />
            {shouldShowFormFields && (
                <PrivacyDisclaimer
                    countryCode={geoIpData?.countryCode}
                    className={styles.disclaimer}
                />
            )}
            {status === RequestStatus.Done && (
                <ToastSuccess
                    className={styles.toast}
                    text={
                        (
                            <p>
                                <TranslatedMessage
                                    id="newsletterSignupConfirmation"
                                    defaultMessage="You're signed up! Check your inbox for an email confirmation or <link>browse all newsletters</link>."
                                    values={{
                                        link: (chunks) => (
                                            <a
                                                href="/newsletters"
                                                target="_blank"
                                                className="underline"
                                                rel="noopener noreferrer"
                                            >
                                                {chunks}
                                            </a>
                                        ),
                                    }}
                                />
                            </p>
                        ) as unknown as string
                    }
                    open={status === RequestStatus.Done}
                />
            )}
        </>
    );
};
